import React, { useEffect, useState, useRef } from 'react';
import { Bell, X } from 'lucide-react';

const STORAGE_KEY = 'notif_prompt_dismissed_until';
const DISMISS_DAYS = 7;

// Petit helper pour accéder à OneSignal une fois prêt
function withOneSignal(fn) {
  if (window.OneSignal && window.OneSignal.Notifications) {
    fn(window.OneSignal);
    return;
  }
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(fn);
}

function NotificationPermissionPrompt() {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const checkedRef = useRef(false);

  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;

    // Override de test : ?testnotif=1 force l'affichage même si déjà granted
    const force = typeof window !== 'undefined' && window.location.search.includes('testnotif=1');

    // Si dismissé récemment et pas en mode test, rien à faire
    if (!force) {
      try {
        const until = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
        if (until && Date.now() < until) {
          console.log('[NotifPrompt] Masqué : encore dismissé jusqu\'à', new Date(until).toLocaleString());
          return;
        }
      } catch {}
    }

    // Si l'API native n'est pas dispo, on n'affiche rien
    if (typeof Notification === 'undefined' || !('Notification' in window)) {
      console.log('[NotifPrompt] Masqué : API Notification non dispo (HTTP ?)');
      return;
    }

    const perm = Notification.permission;
    console.log('[NotifPrompt] Notification.permission =', perm, '| force=', force);

    if (!force && perm !== 'default') {
      // Déjà accordé OU déjà refusé : on ne harcèle pas
      return;
    }

    // Petit délai pour laisser le dashboard se charger avant d'afficher
    const t = setTimeout(() => setShow(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const accept = async () => {
    if (busy) return;
    setBusy(true);
    try {
      withOneSignal(async (OneSignal) => {
        try {
          await OneSignal.Notifications.requestPermission();
        } catch (e) {
          console.error('requestPermission error:', e);
        } finally {
          setShow(false);
          setBusy(false);
        }
      });
    } catch (e) {
      setShow(false);
      setBusy(false);
    }
  };

  const later = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now() + DISMISS_DAYS * 24 * 3600 * 1000));
    } catch {}
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 9999,
        width: 'min(380px, calc(100vw - 32px))',
        background: '#1a1a1d',
        color: '#ffffff',
        border: '1px solid rgba(212, 175, 55, 0.45)',
        borderRadius: 16,
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(102, 126, 234, 0.18)',
        padding: '20px 22px',
        fontFamily: "Inter, system-ui, -apple-system, sans-serif",
        animation: 'slideUpFade .4s ease both'
      }}
    >
      <style>{`
        @keyframes slideUpFade { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
      <button
        onClick={later}
        aria-label="Fermer"
        style={{
          position: 'absolute', top: 10, right: 10,
          background: 'transparent', border: 'none', color: '#888',
          cursor: 'pointer', padding: 4, borderRadius: 4,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = '#888'; }}
      >
        <X size={16} />
      </button>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.35)'
        }}>
          <Bell size={20} color="#fff" />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4, letterSpacing: 0.2 }}>
            Restez informé en temps réel
          </div>
          <div style={{ fontSize: 13, color: '#cfcfd6', lineHeight: 1.55 }}>
            Activez les notifications pour recevoir les nouveaux leads, paiements et alertes importantes — même quand l'onglet est fermé.
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
        <button
          onClick={later}
          style={{
            flex: 1,
            background: 'transparent',
            color: '#cfcfd6',
            border: '1px solid #3a3a40',
            padding: '10px 14px',
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#2a2a30'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          Plus tard
        </button>
        <button
          onClick={accept}
          disabled={busy}
          style={{
            flex: 1.4,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            border: 'none',
            padding: '10px 14px',
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 700,
            cursor: busy ? 'wait' : 'pointer',
            opacity: busy ? 0.7 : 1,
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            boxShadow: '0 6px 18px rgba(102, 126, 234, 0.35)'
          }}
          onMouseEnter={(e) => { if (!busy) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 8px 22px rgba(102, 126, 234, 0.5)'; } }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(102, 126, 234, 0.35)'; }}
        >
          {busy ? 'Demande...' : 'Activer'}
        </button>
      </div>
    </div>
  );
}

export default NotificationPermissionPrompt;
